import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  Engineering,
  Speed,
  Warning,
  TrendingUp,
  Assessment,
  Psychology,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { User } from '../types';
import { Socket } from 'socket.io-client';

interface ComponentDetailProps {
  user: User;
  socket: Socket | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  if (value !== index) {
    return null;
  }

  return (
    <div
      role="tabpanel"
      id={`component-tabpanel-${index}`}
      aria-labelledby={`component-tab-${index}`}
      style={{ padding: '24px' }}
    >
      {children}
    </div>
  );
}

const ComponentDetail: React.FC<ComponentDetailProps> = ({ user, socket }) => {
  const { componentId } = useParams<{ componentId: string }>();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [component, setComponent] = useState<any>(null);
  const [sensorData, setSensorData] = useState<any[]>([]);
  const [healthHistory, setHealthHistory] = useState<any[]>([]);
  const [aiModel, setAiModel] = useState<any>(null);

  useEffect(() => {
    if (componentId) {
      loadComponentData();
    }

    if (socket) {
      socket.on('health_update', handleHealthUpdate);
    }

    return () => {
      if (socket) {
        socket.off('health_update', handleHealthUpdate);
      }
    };
  }, [componentId, socket]);

  const loadComponentData = async () => {
    try {
      setLoading(true);
      
      // Mock data - in production, this would come from API calls
      const mockComponent = {
        id: componentId,
        name: 'Front Bearing',
        type: 'Bearing',
        assetId: 'asset-1',
        assetName: 'My Rotator 6',
        healthIndex: 92.5,
        healthStatus: 'normal',
        sensorCount: 3,
        specifications: {
          manufacturer: 'SKF',
          model: '6308',
          maxSpeed: 8000,
          maxLoad: 5000,
        },
        createdAt: '2025-10-01T10:00:00Z',
        updatedAt: '2025-10-04T10:00:00Z',
      };

      const mockSensorData = [
        { name: 'Temperature', value: 25.5, unit: '°C', status: 'good' },
        { name: 'Vibration', value: 2.1, unit: 'mm/s', status: 'good' },
        { name: 'Current', value: 4.2, unit: 'A', status: 'good' },
      ];

      const mockHealthHistory = [
        { timestamp: '2025-10-01T10:00:00Z', healthIndex: 95.2, status: 'normal' },
        { timestamp: '2025-10-02T10:00:00Z', healthIndex: 94.8, status: 'normal' },
        { timestamp: '2025-10-03T10:00:00Z', healthIndex: 93.5, status: 'normal' },
        { timestamp: '2025-10-04T10:00:00Z', healthIndex: 92.5, status: 'normal' },
      ];

      const mockAiModel = {
        id: 'model-1',
        name: 'Bearing Health Model',
        type: 'health_scoring',
        status: 'active',
        performanceMetrics: {
          accuracy: 94.5,
          precision: 92.1,
          recall: 96.8,
        },
      };

      setComponent(mockComponent);
      setSensorData(mockSensorData);
      setHealthHistory(mockHealthHistory);
      setAiModel(mockAiModel);

    } catch (error) {
      console.error('Error loading component data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleHealthUpdate = (data: any) => {
    if (data.componentId === componentId) {
      setComponent((prev: any) => ({
        ...prev,
        healthIndex: data.healthIndex,
        healthStatus: data.healthStatus,
      }));
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'normal': return 'success';
      case 'caution': return 'warning';
      case 'warning': return 'warning';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading component data...
        </Typography>
      </Container>
    );
  }

  if (!component) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Component not found
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        {component.name}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        {component.type} • {component.assetName} • Health: {component.healthIndex?.toFixed(1)}%
      </Typography>

      <div style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.12)', marginBottom: '24px' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="component tabs">
          <Tab label="Overview" />
          <Tab label="Sensor Data" />
          <Tab label="Health History" />
          <Tab label="AI Analysis" />
        </Tabs>
      </div>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Component Information
                </Typography>
                <Typography variant="body1">
                  <strong>Type:</strong> {component.type}
                </Typography>
                <Typography variant="body1">
                  <strong>Asset:</strong> {component.assetName}
                </Typography>
                <Typography variant="body1">
                  <strong>Sensors:</strong> {component.sensorCount}
                </Typography>
                <Typography variant="body1">
                  <strong>Created:</strong> {new Date(component.createdAt).toLocaleDateString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Health Status
                </Typography>
                <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "16px" }}>
                  <Typography variant="h4" color="primary">
                    {component.healthIndex?.toFixed(1)}%
                  </Typography>
                  <Chip
                    label={component.healthStatus}
                    color={getHealthStatusColor(component.healthStatus)}
                    size="medium"
                  />
                </div>
                <LinearProgress
                  variant="determinate"
                  value={component.healthIndex}
                  sx={{ height: 10, borderRadius: 5 }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Specifications
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(component.specifications).map(([key, value]) => (
                    <Grid item xs={12} sm={6} md={3} key={key}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </Typography>
                        <Typography variant="h6">
                          {value as string}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          {sensorData.map((sensor, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {sensor.name}
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {sensor.value} {sensor.unit}
                  </Typography>
                  <Chip
                    label={sensor.status}
                    color={sensor.status === 'good' ? 'success' : 'error'}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Health Index History
            </Typography>
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={healthHistory}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="timestamp" 
                    tickFormatter={(value) => new Date(value).toLocaleDateString()}
                  />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(value) => new Date(value).toLocaleString()}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="healthIndex" 
                    stroke="#1976d2" 
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  AI Model Status
                </Typography>
                {aiModel ? (
                  <div>
                    <Typography variant="body1">
                      <strong>Model:</strong> {aiModel.name}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Type:</strong> {aiModel.type}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Status:</strong> {aiModel.status}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Accuracy:</strong> {aiModel.performanceMetrics.accuracy}%
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Psychology />}
                      sx={{ mt: 2 }}
                    >
                      Run Analysis
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Alert severity="info">
                      No AI model available for this component
                    </Alert>
                    <Button
                      variant="outlined"
                      startIcon={<Psychology />}
                      sx={{ mt: 2 }}
                      onClick={() => navigate('/ai-models')}
                    >
                      Create AI Model
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  <Button
                    variant="outlined"
                    startIcon={<Assessment />}
                    onClick={() => navigate('/reports')}
                  >
                    Generate Report
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<TrendingUp />}
                  >
                    View Trends
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Warning />}
                  >
                    Set Alerts
                  </Button>
                </div>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Container>
  );
};

export default ComponentDetail;





